import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CircleNotch, Lightning, Target, ClockCounterClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import { DigitPatternContent } from '../components/digit-pattern/DigitPatternContent';
import { W51CombinedPredictions } from '../components/w51/W51CombinedPredictions';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
const API = process.env.REACT_APP_BACKEND_URL;

// Reusable Affinity Detail Card — renders "Last N Occurrences" block for any number
const AffinityCard = ({ aff, ballLabel }) => {
  if (!aff) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`affinity-card-${aff.number}`}>
      <div className="bg-gradient-to-r from-cyan-100 to-transparent px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <span className={`w-14 h-14 rounded-full font-mono font-black text-2xl flex items-center justify-center ${aff.is_lucky_star ? 'bg-yellow-400 text-yellow-900' : 'bg-cyan-500 text-cyan-950'}`}>{aff.number}</span>
          <div>
            {ballLabel && <div className="text-[10px] uppercase tracking-wider text-cyan-700/70 font-bold mb-0.5">{ballLabel}</div>}
            <h2 className="text-lg font-bold">#{aff.number} — Last {aff.occurrence_windows?.length || 3} Occurrences</h2>
            <p className="text-xs text-gray-500">{aff.occurrences} total appearances | Digit: {aff.own_digit}</p>
          </div>
        </div>
      </div>

      {aff.occurrence_windows?.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <h3 className="text-sm font-bold text-gray-600 uppercase mb-3">Last {aff.occurrence_windows.length} Occurrences of #{aff.number} — Digit Breakdown</h3>
          <div className="space-y-5">
            {aff.occurrence_windows.map((occ, oi) => (
              <div key={oi} className="space-y-1.5">
                {occ.map((row, ri) => {
                  const isTarget = row.has_target;
                  return (
                    <div key={`${row.draw_number}-${ri}`} className={`flex items-center gap-4 flex-wrap rounded-lg px-3 py-1.5 ${isTarget ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-gray-50'}`}
                      data-testid={`occ-${aff.number}-${oi}-${row.offset}`}>
                      <div className="w-28 shrink-0">
                        <div className={`text-xs font-bold ${isTarget ? 'text-cyan-700' : 'text-gray-500'}`}>#{row.draw_number}</div>
                        <div className="text-[10px] text-gray-400">{row.draw_date}</div>
                      </div>
                      <div className="flex gap-1.5">
                        {row.balls?.map(b => (
                          <span key={b} className={`w-8 h-8 rounded-full font-mono text-xs font-bold flex items-center justify-center ${b === aff.number ? 'bg-cyan-500 text-cyan-950 ring-1 ring-cyan-300' : isTarget ? 'bg-gray-300 text-gray-700' : 'bg-gray-200/60 text-gray-600'}`}>{b}</span>
                        ))}
                        <span className="text-gray-300 flex items-center text-[10px] mx-0.5">|</span>
                        {row.lucky_stars?.map(ls => (
                          <span key={`ols-${ls}-${oi}-${ri}`} className={`w-8 h-8 rounded-full font-mono text-xs font-bold flex items-center justify-center ${ls === aff.number ? 'bg-yellow-400 text-yellow-900 ring-1 ring-yellow-300' : 'bg-yellow-900/20 text-yellow-700/50'}`}>{ls}</span>
                        ))}
                      </div>
                      <div className="flex gap-1 ml-auto">
                        {[...Array(10)].map((_, digit) => {
                          const cnt = parseInt(row.digits?.[String(digit)] || 0);
                          const isElevatedDigit = aff.digit_affinity?.find(a => a.digit === digit)?.elevated;
                          return (
                            <span key={digit} className={`w-6 h-6 rounded text-[9px] font-mono font-bold flex items-center justify-center ${cnt >= 2 ? (isElevatedDigit ? 'bg-red-500/30 text-red-700' : 'bg-cyan-500/30 text-cyan-700') : cnt === 1 ? (isElevatedDigit ? 'bg-orange-500/15 text-orange-700' : 'bg-gray-100 text-gray-500') : 'bg-gray-50 text-gray-300'}`}>
                              {cnt > 0 ? cnt : '-'}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {oi < aff.occurrence_windows.length - 1 && <div className="border-b border-gray-100 mt-2" />}
              </div>
            ))}
            <div className="border-t border-gray-200 pt-3 flex items-center gap-4">
              <div className="w-28 shrink-0 text-xs font-bold text-gray-700 uppercase">Total</div>
              <div className="flex-1" />
              <div className="flex gap-1 ml-auto">
                {[...Array(10)].map((_, digit) => {
                  const cnt = parseInt(aff.window_digit_totals?.[String(digit)] || 0);
                  const isElevatedDigit = aff.digit_affinity?.find(a => a.digit === digit)?.elevated;
                  const maxCnt = Math.max(...Object.values(aff.window_digit_totals || {}).map(Number));
                  return (
                    <div key={digit} className="text-center">
                      <div className={`w-6 text-[8px] font-bold mb-0.5 ${isElevatedDigit ? 'text-red-700' : cnt === maxCnt ? 'text-cyan-700' : 'text-gray-400'}`}>d{digit}</div>
                      <span className={`w-6 h-6 rounded font-mono text-[10px] font-black flex items-center justify-center ${cnt === maxCnt ? 'bg-emerald-500/25 text-emerald-700 ring-1 ring-emerald-400/30' : isElevatedDigit ? 'bg-red-500/15 text-red-700 ring-1 ring-red-400/20' : cnt >= 5 ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-500'}`}>
                        {cnt}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-4 border-t border-gray-200">
        <h3 className="text-sm font-bold text-gray-600 uppercase mb-3">Most Frequent Numbers in Last {aff.occurrence_windows?.length || 3} Windows</h3>
        <div className="flex flex-wrap gap-2">
          {aff.top_associated_numbers?.map(tn => (
            <div key={tn.number} className={`rounded-lg px-3 py-2 text-center ${tn.count >= 3 ? 'bg-cyan-500/20 border border-cyan-400/30' : 'bg-gray-50'}`}>
              <span className="font-mono font-bold text-lg">{tn.number}</span>
              <span className={`text-[10px] ml-1 ${tn.count >= 3 ? 'text-cyan-600 font-bold' : 'text-gray-500'}`}>({tn.count}x)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Reusable "All 5 Balls" view — renders per-ball AffinityCards + Cross-Ball Companion Totals table
const AllFiveBallsView = ({ sourceData, cd, aff, testId, headerTitle, headerBgClass, headerBorderClass, headerLabelClass, headerDescription, totalsAccentClass, totalsBgClass }) => {
  if (!sourceData || !aff || !cd) return null;
  return (
    <div className="space-y-4" data-testid={testId}>
      <div className={`rounded-xl px-5 py-4 border ${headerBgClass} ${headerBorderClass}`}>
        <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${headerLabelClass}`}>{headerTitle}</div>
        <p className="text-sm text-gray-700">{headerDescription}</p>
      </div>
      {cd.balls?.map((b, idx) => {
        const ballAff = sourceData.number_affinities?.[String(b)];
        if (!ballAff) return null;
        return <AffinityCard key={b} aff={ballAff} ballLabel={`Ball ${idx + 1}`} />;
      })}

      {/* Cross-Ball Companion Totals — aggregate across all 5 balls */}
      {(() => {
        const agg = {};
        (cd.balls || []).forEach((b, idx) => {
          const ballAff = sourceData.number_affinities?.[String(b)];
          if (!ballAff?.top_associated_numbers) return;
          ballAff.top_associated_numbers.forEach(tn => {
            if (!agg[tn.number]) agg[tn.number] = { total: 0, perBall: [] };
            agg[tn.number].total += tn.count;
            agg[tn.number].perBall.push({ ballIdx: idx + 1, ballNum: b, count: tn.count });
          });
        });
        const rows = Object.entries(agg)
          .map(([num, v]) => ({ number: parseInt(num), ...v }))
          .sort((a, b) => b.total - a.total || b.perBall.length - a.perBall.length);
        if (rows.length === 0) return null;
        const maxTotal = rows[0].total;
        return (
          <div className={`rounded-xl border overflow-hidden ${totalsBgClass}`} data-testid={`${testId}-totals`}>
            <div className={`px-5 py-3 border-b ${headerBgClass} ${headerBorderClass}`}>
              <h3 className={`text-sm font-bold uppercase tracking-wider ${totalsAccentClass}`}>Cross-Ball Companion Totals</h3>
              <p className="text-[11px] text-gray-600 mt-0.5">
                Sum of appearances for each companion number across all 5 balls' Last 3 Occurrences windows.
                Higher total = number co-occurs with more of the drawn balls (stronger cross-ball affinity signal).
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2 text-left">Number</th>
                    <th className="px-3 py-2 text-center">B1 ({cd.balls?.[0]})</th>
                    <th className="px-3 py-2 text-center">B2 ({cd.balls?.[1]})</th>
                    <th className="px-3 py-2 text-center">B3 ({cd.balls?.[2]})</th>
                    <th className="px-3 py-2 text-center">B4 ({cd.balls?.[3]})</th>
                    <th className="px-3 py-2 text-center">B5 ({cd.balls?.[4]})</th>
                    <th className="px-3 py-2 text-center">Breakdown</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-left">Balls Hit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const perBallCounts = [0, 0, 0, 0, 0];
                    row.perBall.forEach(pb => { perBallCounts[pb.ballIdx - 1] = pb.count; });
                    const breakdown = perBallCounts.map(c => c || '-').join('+');
                    const hitCount = row.perBall.length;
                    const hitColor = hitCount >= 4 ? `${totalsAccentClass} font-bold` : hitCount === 3 ? 'text-cyan-700 font-bold' : hitCount === 2 ? 'text-amber-700' : 'text-gray-500';
                    const barPct = (row.total / maxTotal) * 100;
                    return (
                      <tr key={row.number} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`${testId}-row-${row.number}`}>
                        <td className="px-3 py-2">
                          <span className="inline-flex w-10 h-10 rounded-full font-mono font-bold text-sm items-center justify-center bg-gray-200 text-gray-900">{row.number}</span>
                        </td>
                        {perBallCounts.map((c, i) => (
                          <td key={`${row.number}-pb-${i}`} className={`px-3 py-2 text-center font-mono ${c >= 3 ? 'text-cyan-700 font-bold' : c >= 1 ? 'text-gray-700' : 'text-gray-300'}`}>{c || '—'}</td>
                        ))}
                        <td className="px-3 py-2 text-center font-mono text-xs text-gray-600">{breakdown}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${totalsAccentClass.replace('text-', 'bg-').replace('-300', '-500/70')}`} style={{ width: `${barPct}%` }} />
                            </div>
                            <span className={`font-mono font-bold text-base ${totalsAccentClass}`}>{row.total}</span>
                          </div>
                        </td>
                        <td className={`px-3 py-2 text-left font-mono text-xs ${hitColor}`}>{hitCount}/5</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-2 bg-gray-50 text-[10px] text-gray-500 flex items-center gap-4 flex-wrap">
              <span>Sorted by total desc → cross-ball count desc</span>
              <span className="flex items-center gap-1"><span className={`font-bold ${totalsAccentClass}`}>4-5/5</span> = strong cross-ball affinity</span>
              <span className="flex items-center gap-1"><span className="text-cyan-700 font-bold">3/5</span> = moderate</span>
              <span className="flex items-center gap-1"><span className="text-amber-700">2/5</span> = weak</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};



// Numbered Block wrapper — matches visual convention used in W3/W7/W14/W24/W31/W59
function Block({ n, title, subtitle, children, testId }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={testId || `w51-block-${n}`}>
      <header className="bg-slate-800 text-white px-4 py-2.5">
        <h3 className="font-bold text-sm tracking-tight">
          <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block {n}</span>
          {title}
        </h3>
        {subtitle && <p className="text-[11px] text-white/70 mt-0.5">{subtitle}</p>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

// Lightweight numbered header that sits above an existing card (non-intrusive block labeling)
function BlockLabel({ n, title, subtitle, testId }) {
  return (
    <div className="bg-slate-800 text-white px-4 py-2.5 rounded-lg -mb-3 shadow-sm" data-testid={testId || `w51-block-${n}-label`}>
      <h3 className="font-bold text-sm tracking-tight">
        <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block {n}</span>
        {title}
      </h3>
      {subtitle && <p className="text-[11px] text-white/70 mt-0.5">{subtitle}</p>}
    </div>
  );
}

export default function WindowDigitAffinity() {
  const [data, setData] = useState(null);
  const [mainsOnlyData, setMainsOnlyData] = useState(null);
  const [backtestData, setBacktestData] = useState(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [likelihoodData, setLikelihoodData] = useState(null);
  const [cooccurData, setCooccurData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisError, setAnalysisError] = useState(null);
  const analysisRequest = useRef(0);
  const [selectedNum, setSelectedNum] = useState(null);
  const [drawNumber, setDrawNumber] = useState('');
  const [activeDrawNum, setActiveDrawNum] = useState(null);
  const [isHistorical, setIsHistorical] = useState(false);
  const [activeTab, setActiveTab] = useState('main'); // 'main' | 'occurrences' | 'occurrences_mains' | 'backtest' | 'distribution'
  const navigate = useNavigate();
  const location = useLocation();

  // Preserve direct links to W51's analysis tabs.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && ['main', 'occurrences', 'occurrences_mains', 'backtest', 'distribution'].includes(t)) {
      setActiveTab(t);
    }
  }, [location.search]);

  const fetchData = (dn) => {
    const requestId = ++analysisRequest.current;
    setLoading(true);
    const url = dn ? `${API}/api/analysis/digit-affinity?draw_number=${dn}` : `${API}/api/analysis/digit-affinity`;
    axios.get(url, { withCredentials: true })
      .then(r => {
        if (requestId !== analysisRequest.current) return;
        if (!r.data?.current_draw || r.data.error) throw new Error('W51 analysis unavailable');
        setAnalysisError(null);
        setData(r.data);
        setMainsOnlyData(null); // invalidate cache on refresh
        setBacktestData(null);
        setLikelihoodData(null);
        setCooccurData(null);
        setActiveDrawNum(dn || r.data?.current_draw?.draw_number);
        setIsHistorical(!!dn);
        if (r.data?.current_draw?.balls?.length > 0) setSelectedNum(String(r.data.current_draw.balls[0]));

        // Fetch digit likelihood ranking alongside (cheap, used in Main Analysis)
        const lhUrl = dn
          ? `${API}/api/analysis/digit-affinity/digit-likelihood?draw_number=${dn}&backtest_n=10&top_k=5`
          : `${API}/api/analysis/digit-affinity/digit-likelihood?backtest_n=10&top_k=5`;
        axios.get(lhUrl, { withCredentials: true })
          .then(lr => { if (requestId === analysisRequest.current) setLikelihoodData(lr.data); })
          .catch(() => {});

        // Fetch co-occurrence backtest (uses Top-3)
        const coocUrl = dn
          ? `${API}/api/analysis/digit-affinity/cooccurrence?draw_number=${dn}&backtest_n=50&top_k=3`
          : `${API}/api/analysis/digit-affinity/cooccurrence?backtest_n=50&top_k=3`;
        axios.get(coocUrl, { withCredentials: true })
          .then(cr => { if (requestId === analysisRequest.current) setCooccurData(cr.data); })
          .catch(() => {});
      })
      .catch(() => { if (requestId === analysisRequest.current) setAnalysisError('Unable to load the requested W51 analysis.'); })
      .finally(() => { if (requestId === analysisRequest.current) setLoading(false); });
  };

  const fetchMainsOnly = (dn) => {
    const params = new URLSearchParams({ scope: 'mains_only' });
    if (dn) params.append('draw_number', String(dn));
    axios.get(`${API}/api/analysis/digit-affinity?${params.toString()}`, { withCredentials: true })
      .then(r => setMainsOnlyData(r.data))
      .catch(() => {});
  };

  const fetchBacktest = (dn) => {
    setBacktestLoading(true);
    const params = new URLSearchParams({ n: '10', top_k: '5' });
    if (dn) params.append('draw_number', String(dn));
    axios.get(`${API}/api/analysis/digit-affinity/backtest?${params.toString()}`, { withCredentials: true })
      .then(r => setBacktestData(r.data))
      .catch(() => {})
      .finally(() => setBacktestLoading(false));
  };

  useEffect(() => { fetchData(null); return () => { analysisRequest.current += 1; }; }, []);
  // The shared event hook passes a refresh flag, not a historical draw number.
  useDrawsUpdated(() => fetchData(isHistorical ? activeDrawNum : null));

  // Lazy-load mains-only data when user switches to that tab
  useEffect(() => {
    if (activeTab === 'occurrences_mains' && !mainsOnlyData && data) {
      fetchMainsOnly(isHistorical ? activeDrawNum : null);
    }
    if (activeTab === 'backtest' && !backtestData && data) {
      fetchBacktest(isHistorical ? activeDrawNum : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, data, mainsOnlyData, backtestData]);

  const handleHistoricalSubmit = (e) => {
    e.preventDefault();
    if (drawNumber.trim()) fetchData(parseInt(drawNumber.trim()));
  };

  const handleReset = () => {
    setDrawNumber('');
    setIsHistorical(false);
    fetchData(null);
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center" role="status" aria-label="Loading W51 analysis" data-testid="w51-analysis-loading"><CircleNotch size={48} className="text-cyan-600 animate-spin" /></div>;
  if (!data) return <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col gap-4 items-center justify-center p-6" role="alert" data-testid="w51-load-error"><p>{analysisError || 'Failed to load W51 analysis.'}</p><button onClick={() => fetchData(null)} className="rounded bg-cyan-700 px-4 py-2 text-sm font-bold text-white" data-testid="w51-load-retry">Retry</button></div>;

  const cd = data.current_draw;
  const nd = data.next_draw;
  const aff = data.number_affinities?.[selectedNum] || null;
  const consensus = data.digit_consensus || [];
  const projected = data.projected_numbers || [];

  // Validation: compute digit hits if next_draw exists
  const nextBalls = nd?.balls || [];
  const nextLS = nd?.lucky_stars || [];
  const nextAllNums = [...nextBalls, ...nextLS];
  const nextDigits = new Set(nextAllNums.map(n => n % 10));
  const elevatedDigits = consensus.filter(c => c.elevated_by_count > 0).map(c => c.digit);
  const strongDigits = consensus.filter(c => c.elevated_by_count >= 2).map(c => c.digit);
  const digitHits = elevatedDigits.filter(d => nextDigits.has(d));
  const strongHits = strongDigits.filter(d => nextDigits.has(d));
  const projectedHits = projected.filter(p => nextBalls.includes(p.number));

  const digitColor = (ratio) => {
    if (ratio >= 1.5) return 'bg-red-500/30 text-red-700 ring-1 ring-red-400/50';
    if (ratio >= 1.25) return 'bg-orange-500/25 text-orange-700';
    if (ratio >= 1.15) return 'bg-yellow-500/20 text-yellow-700';
    return 'bg-gray-50 text-gray-500';
  };

  return (
    <div className={`min-h-screen bg-gray-50 text-gray-900 ${activeTab === 'distribution' ? 'w51-distribution-view' : ''}`}>
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900" data-testid="back-btn"><ArrowLeft size={24} /></button>
            <Lightning size={28} weight="fill" className="text-cyan-600" />
            <div>
              <h1 className="text-lg font-bold text-gray-900" data-testid="w51-title">W51: Digit Affinity Analysis</h1>
              <p className="text-xs text-gray-500">When a number appears, which DIGITS (0-9) cluster in neighboring draws?</p>
            </div>
          </div>
          <form onSubmit={handleHistoricalSubmit} className="flex items-center gap-2" data-testid="historical-form">
            <ClockCounterClockwise size={20} className="text-amber-600" />
            <input
              type="number"
              value={drawNumber}
              onChange={e => setDrawNumber(e.target.value)}
              placeholder="Draw # (e.g. 46107)"
              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 w-44 focus:outline-none focus:ring-1 focus:ring-amber-400"
              data-testid="draw-number-input"
            />
            <button type="submit" className="bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm px-4 py-1.5 rounded-lg transition-colors" data-testid="analyze-historical-btn">
              Backtest
            </button>
            {isHistorical && (
              <button type="button" onClick={handleReset} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-3 py-1.5 rounded-lg transition-colors" data-testid="reset-btn">
                Reset
              </button>
            )}
          </form>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {analysisError && <div className="flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="w51-refresh-error"><span data-testid="w51-refresh-error-message">{analysisError} Showing the last successfully loaded analysis.</span><button onClick={() => fetchData(isHistorical ? activeDrawNum : null)} className="underline font-semibold" data-testid="w51-refresh-retry">Retry</button></div>}
        {activeTab === 'main' && <W51CombinedPredictions predictions={data.projected_numbers} currentDraw={cd} nextDraw={nd} historical={isHistorical} />}
        <WindowNarrative windowId="W51" {...NARRATIVES.W51} className="mb-5" />
        {/* Historical Mode Banner */}
        {isHistorical && (
          <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl px-5 py-3 flex items-center gap-3" data-testid="historical-banner">
            <ClockCounterClockwise size={22} className="text-amber-600 shrink-0" />
            <div className="text-sm">
              <span className="font-bold text-amber-700">Historical Backtest Mode</span>
              <span className="text-gray-500 ml-2">Analyzing Draw #{cd.draw_number} ({cd.draw_date}) — only data up to this draw is used</span>
              {nd && <span className="text-gray-500 ml-2">| Validating against Draw #{nd.draw_number} ({nd.draw_date})</span>}
            </div>
          </div>
        )}

        {/* Window Tabs */}
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1.5 w-fit" data-testid="digit-affinity-tabs">
          <button
            onClick={() => setActiveTab('main')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'main' ? 'bg-cyan-500 text-cyan-950 shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="tab-main-btn"
          >
            Main Analysis
          </button>
          <button
            onClick={() => setActiveTab('occurrences')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'occurrences' ? 'bg-cyan-500 text-cyan-950 shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="tab-occurrences-btn"
          >
            All 5 Balls — Last 3 Occurrences
          </button>
          <button
            onClick={() => setActiveTab('occurrences_mains')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'occurrences_mains' ? 'bg-emerald-500 text-emerald-950 shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="tab-occurrences-mains-btn"
          >
            All 5 Balls — Mains Only (No LS)
          </button>
          <button
            onClick={() => setActiveTab('backtest')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'backtest' ? 'bg-amber-500 text-amber-950 shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="tab-backtest-btn"
          >
            Backtest — Last 10 Draws
          </button>
          <button
            onClick={() => setActiveTab('distribution')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'distribution' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid="tab-distribution-btn"
          >
            Digit Pattern Analysis
          </button>
        </div>

        {/* Block 0 — Validation Panel (historical only) */}
        {activeTab === 'main' && nd && isHistorical && (
          <BlockLabel n={0} title="Prediction Validation" subtitle="Comparing projections against the actual next draw" />
        )}

        {/* VALIDATION PANEL — only when next_draw exists */}
        {activeTab === 'main' && nd && isHistorical && (
          <div className="bg-emerald-50 rounded-xl border border-emerald-300 p-6 space-y-5" data-testid="validation-panel">
            <div className="flex items-center gap-3 mb-1">
              <CheckCircle size={26} weight="fill" className="text-emerald-600" />
              <div>
                <h2 className="text-lg font-bold">Prediction Validation — Draw #{nd.draw_number} ({nd.draw_date})</h2>
                <p className="text-xs text-gray-500">Comparing digit affinity projections from Draw #{cd.draw_number} against actual results</p>
              </div>
            </div>

            {/* Actual Next Draw Numbers */}
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Actual Next Draw Numbers</div>
              <div className="flex items-center gap-3 flex-wrap">
                {nextBalls.map(b => (
                  <div key={b} className="w-12 h-12 rounded-full bg-emerald-500/30 border-2 border-emerald-400 font-mono font-bold text-lg flex items-center justify-center text-emerald-700" data-testid={`actual-ball-${b}`}>
                    {b}
                  </div>
                ))}
                <span className="text-gray-300 mx-1">|</span>
                {nextLS.map(ls => (
                  <div key={`nls-${ls}`} className="w-12 h-12 rounded-full bg-yellow-500/20 border-2 border-yellow-400/60 font-mono font-bold text-lg flex items-center justify-center text-yellow-700" data-testid={`actual-ls-${ls}`}>
                    {ls}
                  </div>
                ))}
                <div className="ml-3 text-xs text-gray-400">Digits: [{[...nextDigits].sort((a,b) => a-b).join(', ')}]</div>
              </div>
            </div>

            {/* Digit Hit Analysis */}
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Digit Detection Rate</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-mono font-black text-emerald-700">{digitHits.length}/{nextDigits.size}</div>
                  <div className="text-[10px] text-gray-500 mt-1">All Elevated Digits Hit</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-mono font-black text-amber-700">{strongHits.length}/{nextDigits.size}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Strong (2+n) Digits Hit</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-mono font-black text-cyan-700">{projectedHits.length}/{nextBalls.length}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Projected Numbers Hit</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-mono font-black text-gray-600">{data.total_draws_analyzed}</div>
                  <div className="text-[10px] text-gray-500 mt-1">Draws Analyzed</div>
                </div>
              </div>
            </div>

            {/* Per-Digit Breakdown */}
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Per-Digit Breakdown (actual next-draw digits)</div>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {[...Array(10)].map((_, digit) => {
                  const inNext = nextDigits.has(digit);
                  const cEntry = consensus.find(c => c.digit === digit);
                  const wasElevated = cEntry && cEntry.elevated_by_count > 0;
                  const wasStrong = cEntry && cEntry.elevated_by_count >= 2;
                  const bg = inNext && wasStrong ? 'bg-emerald-500/30 border-emerald-400/60 ring-1 ring-emerald-400/40'
                    : inNext && wasElevated ? 'bg-emerald-500/20 border-emerald-400/30'
                    : inNext && !wasElevated ? 'bg-red-500/15 border-red-400/30'
                    : !inNext && wasElevated ? 'bg-amber-500/10 border-amber-400/20'
                    : 'bg-gray-50 border-gray-200';
                  return (
                    <div key={digit} className={`rounded-lg p-3 text-center border ${bg}`} data-testid={`validation-digit-${digit}`}>
                      <div className="text-xl font-mono font-black">{digit}</div>
                      <div className="mt-1">
                        {inNext && wasElevated && <CheckCircle size={16} weight="fill" className="text-emerald-600 mx-auto" />}
                        {inNext && !wasElevated && <XCircle size={16} weight="fill" className="text-red-600 mx-auto" />}
                        {!inNext && wasElevated && <span className="text-[9px] text-amber-700">MISS</span>}
                        {!inNext && !wasElevated && <span className="text-[9px] text-gray-300">-</span>}
                      </div>
                      <div className="text-[9px] text-gray-400 mt-1">
                        {wasStrong ? `${cEntry.elevated_by_count}n` : wasElevated ? '1n' : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-3 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-400/60 inline-block" /> Correct: elevated + appeared</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/15 border border-red-400/30 inline-block" /> Missed: appeared but not elevated</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/10 border border-amber-400/20 inline-block" /> False positive: elevated but didn't appear</span>
              </div>
            </div>

            {/* Projected Number Hits */}
            {projectedHits.length > 0 && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Projected Numbers That Hit</div>
                <div className="flex flex-wrap gap-2">
                  {projectedHits.map(p => (
                    <div key={p.number} className="bg-emerald-500/25 border border-emerald-400/50 rounded-lg px-4 py-2 text-center" data-testid={`hit-projected-${p.number}`}>
                      <div className="text-xl font-mono font-black text-emerald-700">{p.number}</div>
                      <div className="text-[9px] text-gray-500">d{p.digit} | {p.elevated_by}n | {p.historical_count}x</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Block 1 — Current Draw Snapshot */}
        {activeTab === 'main' && <BlockLabel n={1} title="Current Draw Snapshot" subtitle="Click any ball/star to drill-down its digit affinity" />}

        {/* Current Draw */}
        <div className="bg-white rounded-xl p-4 border border-gray-200" data-testid="current-draw">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">{isHistorical ? 'Analyzed' : 'Current'} Draw #{cd.draw_number} — {cd.draw_date}</div>
          <div className="flex items-center gap-3 flex-wrap">
            {cd.balls?.map(b => (
              <button key={b} onClick={() => setSelectedNum(String(b))}
                className={`w-12 h-12 rounded-full font-mono font-bold text-lg flex items-center justify-center transition-all ${selectedNum === String(b) ? 'bg-cyan-500 text-cyan-950 ring-2 ring-cyan-300 scale-110' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`}
                data-testid={`select-ball-${b}`}>
                {b}
              </button>
            ))}
            <span className="text-gray-300 mx-2">|</span>
            {cd.lucky_stars?.map(ls => (
              <button key={`ls-${ls}`} onClick={() => setSelectedNum(String(ls))}
                className={`w-12 h-12 rounded-full font-mono font-bold text-lg flex items-center justify-center transition-all ${selectedNum === String(ls) ? 'bg-yellow-400 text-yellow-900 ring-2 ring-yellow-300 scale-110' : 'bg-yellow-900/40 text-yellow-700 hover:bg-yellow-800/60'}`}
                data-testid={`select-ls-${ls}`}>
                {ls}
              </button>
            ))}
          </div>
        </div>

        {/* Block 2 — Digit Likelihood Ranking */}
        {activeTab === 'main' && likelihoodData && (
          <BlockLabel n={2} title="Next-Draw Digit Likelihood Ranking" subtitle="Combined affinity × backtest hit-rate score (0–100)" />
        )}

        {/* DIGIT LIKELIHOOD RANKING — Main Analysis tab only */}
        {activeTab === 'main' && likelihoodData && (
          <div className="bg-white rounded-xl border-2 border-fuchsia-400 overflow-hidden" data-testid="digit-likelihood-card">
            <div className="px-6 py-4 border-b border-fuchsia-500/30 bg-fuchsia-900/20">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-base font-black uppercase tracking-wider text-fuchsia-700">Next Draw Digit Likelihood Ranking</h2>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Combines <span className="text-fuchsia-700 font-bold">current affinity</span> (55% weight) × <span className="text-cyan-700 font-bold">backtest hit rate</span> from last {likelihoodData.backtest_n} draws (45% weight).
                    Based on Mains-Only scope (Lucky Star noise excluded) + digit-family matching (64% historical hit rate).
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-fuchsia-700/70 uppercase tracking-wider">Pivot</div>
                  <div className="text-sm font-mono font-bold text-gray-900">#{likelihoodData.pivot_draw_number}</div>
                  <div className="text-[10px] text-gray-500">{likelihoodData.pivot_draw_date}</div>
                </div>
              </div>
            </div>

            {/* Top-3 Digit callouts */}
            <div className="px-6 py-4 bg-gradient-to-r from-fuchsia-100 to-transparent">
              <div className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-700/80 mb-2">🔥 Strongest Digit Signals for Next Draw</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {likelihoodData.ranking.slice(0, 3).map((r) => {
                  const recColor = r.recommendation === 'STRONG' ? 'from-emerald-500/30 to-emerald-600/20 border-emerald-400/50 text-emerald-700'
                    : r.recommendation === 'MODERATE' ? 'from-cyan-500/30 to-blue-600/20 border-cyan-400/50 text-cyan-700'
                    : 'from-gray-500/20 to-gray-600/10 border-gray-300/30 text-gray-700';
                  return (
                    <div key={r.digit} className={`rounded-xl border bg-gradient-to-br p-4 ${recColor}`} data-testid={`likelihood-top-${r.digit}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-12 h-12 rounded-full bg-gray-200 font-mono font-black text-2xl flex items-center justify-center">{r.digit}</span>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider opacity-70">Rank #{r.rank}</div>
                            <div className="text-xs font-bold">{r.recommendation}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-mono font-black">{(r.combined_score * 100).toFixed(0)}</div>
                          <div className="text-[9px] opacity-60 uppercase">Score /100</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-3 text-[10px]">
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <div className="opacity-60">Affinity</div>
                          <div className="font-mono font-bold text-base">{r.affinity_score}</div>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <div className="opacity-60">Backtest Hit</div>
                          <div className="font-mono font-bold text-base">{r.backtest_hit_rate_pct !== null ? `${r.backtest_hit_rate_pct}%` : 'n/a'}</div>
                        </div>
                      </div>
                      <div className="text-[10px] opacity-70 mb-1">Candidate Numbers (excl pivot):</div>
                      <div className="flex flex-wrap gap-1">
                        {r.candidates_main_excluding_pivot.map(n => (
                          <span key={n} className="inline-flex w-8 h-8 rounded-full bg-gray-200 text-gray-900 font-mono font-bold text-xs items-center justify-center">{n}</span>
                        ))}
                        {r.candidates_ls_excluding_pivot.length > 0 && <span className="self-center text-gray-400">|</span>}
                        {r.candidates_ls_excluding_pivot.map(ls => (
                          <span key={`ls-${ls}`} className="inline-flex w-8 h-8 rounded-full bg-yellow-500/30 text-yellow-700 font-mono font-bold text-xs items-center justify-center ring-1 ring-yellow-400/40">{ls}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Full ranking table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="likelihood-table">
                <thead className="bg-gray-100">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-fuchsia-500/20">
                    <th className="px-3 py-2 text-center">Rank</th>
                    <th className="px-3 py-2 text-center">Digit</th>
                    <th className="px-3 py-2 text-center">Affinity</th>
                    <th className="px-3 py-2 text-center">Backtest Hit</th>
                    <th className="px-3 py-2 text-center">Combined</th>
                    <th className="px-3 py-2 text-center">Verdict</th>
                    <th className="px-3 py-2 text-left">Main Candidates (1–50, excl pivot)</th>
                    <th className="px-3 py-2 text-left">LS Candidates (1–12)</th>
                  </tr>
                </thead>
                <tbody>
                  {likelihoodData.ranking.map(r => {
                    const recBg = r.recommendation === 'STRONG' ? 'bg-emerald-500/20 text-emerald-700 ring-1 ring-emerald-400/40'
                      : r.recommendation === 'MODERATE' ? 'bg-cyan-500/20 text-cyan-700 ring-1 ring-cyan-400/40'
                      : r.recommendation === 'AVOID' ? 'bg-red-500/20 text-red-700 ring-1 ring-red-400/40'
                      : 'bg-gray-50 text-gray-500 ring-1 ring-white/10';
                    const rowBg = r.rank <= 3 ? 'bg-fuchsia-900/10' : 'hover:bg-gray-50';
                    return (
                      <tr key={r.digit} className={`border-b border-gray-100 ${rowBg}`} data-testid={`likelihood-row-${r.digit}`}>
                        <td className="px-3 py-2 text-center font-mono font-bold text-gray-700">{r.rank}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex w-9 h-9 rounded-full bg-fuchsia-500/30 text-fuchsia-700 font-mono font-black text-base items-center justify-center ring-1 ring-fuchsia-400/40">{r.digit}</span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono font-bold text-gray-900">{r.affinity_score}</td>
                        <td className="px-3 py-2 text-center font-mono">
                          {r.backtest_hit_rate_pct !== null ? (
                            <span className={r.backtest_hit_rate_pct >= 70 ? 'text-emerald-700 font-bold' : r.backtest_hit_rate_pct >= 50 ? 'text-cyan-700' : 'text-amber-700'}>
                              {r.backtest_hit_rate_pct}%
                            </span>
                          ) : <span className="text-gray-400">n/a</span>}
                          <div className="text-[9px] text-gray-500">({r.backtest_hits}/{r.backtest_samples})</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-fuchsia-500/80 rounded-full" style={{ width: `${r.combined_score * 100}%` }} />
                            </div>
                            <span className="font-mono font-bold text-fuchsia-700 text-sm w-10 text-right">{(r.combined_score * 100).toFixed(0)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block rounded px-2 py-0.5 font-mono text-[10px] font-bold ${recBg}`}>{r.recommendation}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {r.candidates_main_excluding_pivot.map(n => (
                              <span key={n} className="inline-flex w-7 h-7 rounded-full bg-gray-200 text-gray-900 font-mono text-[10px] font-bold items-center justify-center">{n}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {r.candidates_ls_excluding_pivot.map(ls => (
                              <span key={`ls-${ls}`} className="inline-flex w-7 h-7 rounded-full bg-yellow-600/40 text-yellow-700 font-mono text-[10px] font-bold items-center justify-center">{ls}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-fuchsia-500/20 flex items-center gap-4 flex-wrap text-[10px] text-gray-500">
              <span><span className="text-emerald-700 font-bold">STRONG</span> = rank ≤3 AND combined ≥55</span>
              <span><span className="text-cyan-700 font-bold">MODERATE</span> = rank ≤5 AND combined ≥45</span>
              <span><span className="text-red-700 font-bold">AVOID</span> = rank ≥8</span>
              <span className="ml-auto italic">Combined = 0.55 × (affinity / max) + 0.45 × (backtest hit rate)</span>
            </div>
          </div>
        )}

        {/* Block 3 — Top-3 Co-Occurrence Probability */}
        {activeTab === 'main' && cooccurData && (
          <BlockLabel n={3} title="Top-3 Co-Occurrence Probability" subtitle="Joint probability that the Top-3 ranked digits appear together in the next draw" />
        )}

        {/* TOP-3 CO-OCCURRENCE PROBABILITY — Main Analysis tab only */}
        {activeTab === 'main' && cooccurData && cooccurData.current_top_digits && (
          <div className="bg-white rounded-xl border-2 border-indigo-400 overflow-hidden" data-testid="cooccurrence-card">
            <div className="px-6 py-4 border-b border-indigo-400/30 bg-indigo-900/30">
              <h2 className="text-base font-black uppercase tracking-wider text-indigo-700">How Likely Are All 3 Top Digits Together?</h2>
              <p className="text-xs text-gray-600 mt-0.5">
                Current Top-{cooccurData.top_k} digits: <span className="font-mono font-bold text-indigo-700">d{cooccurData.current_top_digits.join(', d')}</span>. Backtested across last <span className="font-bold">{cooccurData.backtest_pivots}</span> historical pivots — how often did their digit families all appear in the next draw (including LS).
              </p>
            </div>

            {/* Distribution bars */}
            <div className="px-6 py-5 space-y-3 bg-gradient-to-r from-indigo-100 to-transparent">
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700/80 mb-2">Probability Distribution — Of {cooccurData.top_k} Top Digits, How Many Hit?</div>
              {cooccurData.hits_distribution.map((count, k) => {
                const pct = cooccurData.distribution_pct[k];
                const isAllHit = k === cooccurData.top_k;
                const barColor = k === 0 ? 'bg-red-500/70' : k === 1 ? 'bg-amber-500/70' : k === 2 ? 'bg-cyan-500/70' : 'bg-emerald-500/80';
                const labelColor = k === 0 ? 'text-red-700' : k === 1 ? 'text-amber-700' : k === 2 ? 'text-cyan-700' : 'text-emerald-700';
                return (
                  <div key={`cooccur-${k}`} className={`flex items-center gap-3 ${isAllHit ? 'ring-2 ring-emerald-400/40 rounded-lg px-2 py-1 bg-emerald-500/5' : ''}`}>
                    <div className="w-32 shrink-0">
                      <div className={`text-sm font-bold ${labelColor}`}>{k}/{cooccurData.top_k} digits hit</div>
                      {isAllHit && <div className="text-[9px] text-emerald-700/70 font-bold uppercase">🎯 Full sweep</div>}
                      {k === 0 && <div className="text-[9px] text-red-700/70 uppercase">complete miss</div>}
                    </div>
                    <div className="flex-1 h-5 bg-gray-50 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${barColor} flex items-center justify-end pr-2 transition-all`} style={{ width: `${Math.max(pct, 4)}%` }}>
                        {pct >= 8 && <span className="text-[10px] font-mono font-bold text-gray-900">{pct}%</span>}
                      </div>
                    </div>
                    <div className="w-24 text-right">
                      {pct < 8 && <span className="font-mono text-sm font-bold text-gray-700">{pct}%</span>}
                      <span className="text-[10px] text-gray-500 ml-1">({count}/{cooccurData.backtest_pivots})</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Subset joint probabilities */}
            <div className="px-6 py-4 border-t border-indigo-400/20 bg-indigo-950/30">
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700/80 mb-2">Historical Joint Probability — Every Subset of Current Top-{cooccurData.top_k} ({cooccurData.current_top_digits.map(d => `d${d}`).join(', ')})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {Object.entries(cooccurData.current_subset_probs)
                  .sort((a, b) => a[1].size - b[1].size || b[1].historical_pct - a[1].historical_pct)
                  .map(([key, v]) => {
                    const sizeColor = v.size === 1 ? 'border-cyan-500/40 bg-cyan-500/10'
                      : v.size === 2 ? 'border-violet-500/40 bg-violet-500/10'
                      : 'border-emerald-500/40 bg-emerald-500/10';
                    const pctColor = v.historical_pct >= 60 ? 'text-emerald-700'
                      : v.historical_pct >= 40 ? 'text-cyan-700'
                      : v.historical_pct >= 20 ? 'text-amber-700' : 'text-red-700';
                    return (
                      <div key={key} className={`rounded-lg border px-3 py-2 ${sizeColor}`} data-testid={`cooc-subset-${key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex gap-1">
                            {v.combo.map(d => (
                              <span key={d} className="inline-flex w-7 h-7 rounded-full bg-gray-200 text-gray-900 font-mono font-black text-xs items-center justify-center">{d}</span>
                            ))}
                          </div>
                          <div className="text-right">
                            <div className={`font-mono font-black text-lg ${pctColor}`}>{v.historical_pct}%</div>
                            <div className="text-[9px] text-gray-500">{v.joint_hits}/{v.seen_as_topK}</div>
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-1">
                          {v.size === 1 ? 'individual hit rate' : `all ${v.size} together`}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Rank-slot breakdown */}
            <div className="px-6 py-4 border-t border-indigo-400/20 flex items-center gap-4 flex-wrap">
              <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700/80">Hit Rate by Rank Slot</div>
              {cooccurData.rank_slot_hit_rate.map(slot => (
                <div key={slot.slot} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                  <span className="inline-flex w-6 h-6 rounded-full bg-indigo-500/40 text-indigo-700 font-mono font-bold text-xs items-center justify-center">#{slot.slot}</span>
                  <span className="font-mono font-bold text-sm text-indigo-700">{slot.hit_rate_pct}%</span>
                  <span className="text-[10px] text-gray-500">({slot.hits}/{slot.seen})</span>
                </div>
              ))}
              <div className="ml-auto text-[11px] text-gray-500 italic max-w-md text-right">
                💡 Rank-1 digit hits {cooccurData.rank_slot_hit_rate[0]?.hit_rate_pct}% of the time — Top-3 individually reliable, but rarely all 3 together.
              </div>
            </div>
          </div>
        )}

        {/* FINAL VERDICT TABLE — spreadsheet-style consolidated recommendation */}
        {activeTab === 'main' && likelihoodData && cooccurData && (() => {
          const ps = data.pattern_signals || {};
          // Build rows
          const rows = [];
          // Digit rows — Top-3 likelihood, sorted by backtest hit % desc
          const top3 = [...likelihoodData.ranking.slice(0, 3)].sort((a, b) => (b.backtest_hit_rate_pct || 0) - (a.backtest_hit_rate_pct || 0));
          top3.forEach((r, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === top3.length - 1;
            rows.push({
              type: 'Digit',
              projected: String(r.digit),
              pct: r.backtest_hit_rate_pct !== null ? `${r.backtest_hit_rate_pct}%` : '—',
              pctVal: r.backtest_hit_rate_pct || 0,
              expected: r.candidates_main_excluding_pivot.join(', '),
              summary: isFirst
                ? `Required: one number ending in ${r.digit}`
                : isLast
                ? `Preferred: one number ending in ${r.digit}`
                : `Required: one number ending in ${r.digit}`,
              tone: 'digit',
            });
          });
          // Pair joints — only 2-digit subsets, sorted by pct desc, top 2
          const pairs = Object.values(cooccurData.current_subset_probs || {})
            .filter(s => s.size === 2)
            .sort((a, b) => b.historical_pct - a.historical_pct)
            .slice(0, 2);
          pairs.forEach(p => {
            rows.push({
              type: 'Pair Joint',
              projected: p.combo.join('+'),
              pct: `${p.historical_pct}%`,
              pctVal: p.historical_pct,
              expected: '—',
              summary: p.historical_pct >= 60
                ? `Strong pair: require both digits (${p.combo.join(' AND ')}) in ticket`
                : `Moderate pair: cover ${p.combo.join('+')} across multiple tickets`,
              tone: 'pair',
            });
          });
          // Triple joint (if Top-3 historically ever co-hit)
          const triple = Object.values(cooccurData.current_subset_probs || {}).find(s => s.size === 3);
          if (triple) {
            rows.push({
              type: 'Triple Joint',
              projected: triple.combo.join('+'),
              pct: `${triple.historical_pct}%`,
              pctVal: triple.historical_pct,
              expected: '—',
              summary: triple.historical_pct >= 30
                ? `Stretch: try covering all 3 digits in one ticket`
                : `Unreliable: don't force all 3 together`,
              tone: 'triple',
            });
          }
          // Hottest digits
          const hotDigits = ps.hottest_digits || [];
          if (hotDigits.length > 0) {
            const hotNums = hotDigits.flatMap(d => d === 0 ? [10, 20, 30, 40, 50] : Array.from({ length: 5 }, (_, i) => d + i * 10).filter(n => n >= 1 && n <= 50));
            rows.push({
              type: 'Hottest digit',
              projected: hotDigits.join(','),
              pct: '—',
              pctVal: 0,
              expected: hotNums.slice(0, 12).join(', '),
              summary: `Momentum signal (last 5 draws) — consider if any overlap with Top-3 above`,
              tone: 'hot',
            });
          }
          // Coldest digits
          const coldDigits = ps.coldest_digits || [];
          if (coldDigits.length > 0) {
            rows.push({
              type: 'Coldest digit',
              projected: coldDigits.join(','),
              pct: '—',
              pctVal: 0,
              expected: '—',
              summary: `Mean-reversion signal (underrepresented). Can be filler but not primary.`,
              tone: 'cold',
            });
          }
          // Avoid row (from AVOID tier in likelihood)
          const avoidDigits = likelihoodData.ranking.filter(r => r.recommendation === 'AVOID').map(r => r.digit);
          if (avoidDigits.length > 0) {
            rows.push({
              type: 'Avoid',
              projected: avoidDigits.join(','),
              pct: '—',
              pctVal: 0,
              expected: '—',
              summary: `Avoid unless filling: digits ${avoidDigits.join(', ')} (low backtest reliability)`,
              tone: 'avoid',
            });
          }

          const toneStyles = {
            digit: 'bg-emerald-900/20 border-l-2 border-emerald-400/60',
            pair: 'bg-violet-900/20 border-l-2 border-violet-400/60',
            triple: 'bg-indigo-900/20 border-l-2 border-indigo-400/60',
            hot: 'bg-red-900/10 border-l-2 border-red-400/40',
            cold: 'bg-blue-900/10 border-l-2 border-blue-400/40',
            avoid: 'bg-rose-900/20 border-l-2 border-rose-500/50',
          };

          return (
            <>
            <BlockLabel n={4} title="Final Verdict — Next Draw Digit Predictions" subtitle="Consolidated spreadsheet: digits, pairs & triples with backtest %" />
            <div className="bg-white rounded-xl border-2 border-emerald-400/40 overflow-hidden shadow-2xl" data-testid="final-verdict-table">
              <div className="px-6 py-4 bg-gradient-to-r from-emerald-100 to-teal-50 border-b border-emerald-300">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="text-base font-black uppercase tracking-wider text-emerald-700">🎯 Final Verdict — Next Draw Digit Predictions</h2>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Consolidated spreadsheet view. Digit rows sorted by backtest hit % desc. Pair-joint % is historical co-occurrence when both were in Top-3.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-emerald-700/70 uppercase tracking-wider">Pivot</div>
                    <div className="font-mono font-bold text-gray-900">#{likelihoodData.pivot_draw_number}</div>
                    <div className="text-[10px] text-gray-500">{likelihoodData.pivot_draw_date}</div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100/70">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-600 border-b border-gray-200">
                      <th className="px-3 py-2 text-center w-20">Window</th>
                      <th className="px-3 py-2 text-center w-20">Projection</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left w-28">Projection</th>
                      <th className="px-3 py-2 text-center w-28">Projected</th>
                      <th className="px-3 py-2 text-center w-24">% Chance</th>
                      <th className="px-3 py-2 text-left">Expected Numbers</th>
                      <th className="px-3 py-2 text-left w-96">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={`verdict-${r.type}-${r.projected}-${idx}`} className={`border-b border-gray-100 ${toneStyles[r.tone]}`} data-testid={`verdict-row-${r.type.toLowerCase().replace(' ', '-')}-${r.projected}`}>
                        <td className="px-3 py-2 text-center font-mono font-bold text-emerald-700">51</td>
                        <td className="px-3 py-2 text-center font-mono text-gray-700">1</td>
                        <td className="px-3 py-2 font-bold text-gray-800">Digit Affinity Analysis</td>
                        <td className="px-3 py-2 font-mono text-gray-700">{r.type}</td>
                        <td className="px-3 py-2 text-center">
                          {r.type === 'Digit' ? (
                            <span className="inline-flex w-9 h-9 rounded-full bg-emerald-500/30 text-emerald-700 font-mono font-black text-base items-center justify-center ring-1 ring-emerald-400/40">{r.projected}</span>
                          ) : r.type === 'Pair Joint' || r.type === 'Triple Joint' ? (
                            <span className="font-mono font-bold text-violet-700">{r.projected}</span>
                          ) : (
                            <span className="font-mono font-bold text-gray-900">{r.projected}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`font-mono font-black text-base ${r.pctVal >= 60 ? 'text-emerald-700' : r.pctVal >= 40 ? 'text-cyan-700' : r.pctVal > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                            {r.pct}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-700 text-[11px]">{r.expected}</td>
                        <td className="px-3 py-2 text-gray-700 italic">{r.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-3 bg-gray-100 border-t border-gray-200 text-[11px] text-gray-500 flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-500/30 ring-1 ring-emerald-400" /> Top-3 digit predictions</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-violet-500/30 ring-1 ring-violet-400" /> Pair joint probability</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-500/20 ring-1 ring-red-400/40" /> Hot/cold momentum (info)</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-rose-500/30 ring-1 ring-rose-500" /> Avoid</span>
                <span className="ml-auto italic">% Chance = backtest hit rate (digit rows) / joint historical co-hit (pair rows)</span>
              </div>
            </div>
            </>
          );
        })()}

        {/* Block 5 — Last 5 Draws Digit Frequency */}
        {activeTab === 'main' && data.last_5_draws?.length > 0 && (
          <BlockLabel n={5} title="Last 5 Draws — Digit Frequency" subtitle="Per-draw and aggregate digit counts, plus hot/cold projections" />
        )}

        {/* LAST 5 DRAWS — Digit Frequency */}
        {activeTab === 'main' && data.last_5_draws?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="last-5-draws">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-100/50">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-600">Last 5 Draws — Digit Frequency</h2>
            </div>
            <div className="p-5 space-y-4">
              {/* Per-draw rows */}
              {data.last_5_draws.map(dd => (
                <div key={dd.draw_number} className="flex items-center gap-4 flex-wrap" data-testid={`last5-draw-${dd.draw_number}`}>
                  <div className="w-36 shrink-0">
                    <div className="text-xs font-bold text-gray-500">#{dd.draw_number}</div>
                    <div className="text-[10px] text-gray-400">{dd.draw_date}</div>
                  </div>
                  <div className="flex gap-1.5">
                    {dd.balls?.map(b => (
                      <span key={b} className="w-9 h-9 rounded-full bg-gray-200 font-mono text-sm font-bold flex items-center justify-center text-gray-700">{b}</span>
                    ))}
                    <span className="text-gray-300 flex items-center mx-0.5">|</span>
                    {dd.lucky_stars?.map(ls => (
                      <span key={`ls5-${ls}`} className="w-9 h-9 rounded-full bg-yellow-900/30 font-mono text-sm font-bold flex items-center justify-center text-yellow-700/70">{ls}</span>
                    ))}
                  </div>
                  <div className="flex gap-1 ml-auto">
                    {[...Array(10)].map((_, digit) => {
                      const cnt = parseInt(dd.digits?.[String(digit)] || 0);
                      return (
                        <span key={digit} className={`w-7 h-7 rounded text-[10px] font-mono font-bold flex items-center justify-center ${cnt >= 2 ? 'bg-cyan-500/30 text-cyan-700' : cnt === 1 ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-300'}`}>
                          {cnt > 0 ? cnt : '-'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
              {/* Totals row */}
              <div className="border-t border-gray-200 pt-3 flex items-center gap-4">
                <div className="w-36 shrink-0 text-xs font-bold text-gray-700 uppercase">Total (5 draws)</div>
                <div className="flex-1" />
                <div className="flex gap-1 ml-auto">
                  {[...Array(10)].map((_, digit) => {
                    const cnt = parseInt(data.last_5_digit_totals?.[String(digit)] || 0);
                    return (
                      <div key={digit} className="text-center">
                        <div className={`w-7 text-[9px] font-bold mb-0.5 ${cnt === 0 ? 'text-red-600' : cnt >= 6 ? 'text-cyan-700' : 'text-gray-500'}`}>d{digit}</div>
                        <span className={`w-7 h-7 rounded font-mono text-sm font-black flex items-center justify-center ${cnt === 0 ? 'bg-red-500/20 text-red-600 ring-1 ring-red-400/30' : cnt >= 6 ? 'bg-cyan-500/25 text-cyan-700 ring-1 ring-cyan-400/30' : cnt >= 3 ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-500'}`}>
                          {cnt}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Hottest & Coldest Digit Projections */}
              {data.pattern_signals && (() => {
                const ps = data.pattern_signals;
                const hottestNums = ps.hottest_digits?.flatMap(d => d === 0 ? [10,20,30,40,50] : Array.from({length: 5}, (_, i) => d + i * 10).filter(n => n >= 1 && n <= 50)) || [];
                const coldestNums = ps.coldest_digits?.flatMap(d => d === 0 ? [10,20,30,40,50] : Array.from({length: 5}, (_, i) => d + i * 10).filter(n => n >= 1 && n <= 50)) || [];
                const currBalls = cd.balls || [];
                return (
                  <div className="border-t border-gray-200 pt-4 mt-1 space-y-4">
                    {/* Coldest — most prominent */}
                    <div className="bg-blue-50 rounded-xl border border-blue-300 p-4" data-testid="coldest-projection">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex gap-2">
                          {ps.coldest_digits?.map(d => (
                            <div key={d} className="w-12 h-12 rounded-xl bg-blue-500/25 border-2 border-blue-400/50 font-mono font-black text-xl flex items-center justify-center text-blue-700">{d}</div>
                          ))}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-blue-700">Coldest Digit — Only {ps.coldest_count}x in 5 draws</div>
                          <div className="text-[10px] text-gray-500">Mean reversion: lowest frequency digit has highest chance of bouncing back</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Projected Numbers (digit {ps.coldest_digits?.join(', ')})</div>
                      <div className="flex gap-2 flex-wrap">
                        {coldestNums.map(n => {
                          const isCurrent = currBalls.includes(n);
                          const hitNext = nd && (nd.balls || []).includes(n);
                          return (
                            <div key={n} className={`w-12 h-12 rounded-full font-mono font-bold text-lg flex items-center justify-center transition-all ${hitNext ? 'bg-emerald-500/30 border-2 border-emerald-400 text-emerald-700 ring-1 ring-emerald-400/30' : isCurrent ? 'bg-gray-50 border border-gray-200 text-gray-300' : 'bg-blue-500/15 border-2 border-blue-400/30 text-blue-700'}`}
                              data-testid={`cold-num-${n}`}>
                              {n}
                            </div>
                          );
                        })}
                        {nd && (() => {
                          const hits = coldestNums.filter(n => (nd.balls || []).includes(n));
                          return hits.length > 0 ? (
                            <div className="flex items-center ml-2">
                              <span className="text-xs font-bold text-emerald-600">{hits.length} HIT</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Hottest */}
                    <div className="bg-red-50 rounded-xl border border-red-300 p-4" data-testid="hottest-projection">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex gap-2">
                          {ps.hottest_digits?.map(d => (
                            <div key={d} className="w-12 h-12 rounded-xl bg-red-500/20 border-2 border-red-400/40 font-mono font-black text-xl flex items-center justify-center text-red-700">{d}</div>
                          ))}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-red-700">Hottest Digit — {ps.hottest_count}x in 5 draws <span className="text-emerald-600 text-[10px] ml-1">75% hit rate</span></div>
                          <div className="text-[10px] text-gray-500">Momentum: highest frequency digit continues into next draw</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Projected Numbers (digit {ps.hottest_digits?.join(', ')})</div>
                      <div className="flex gap-2 flex-wrap">
                        {hottestNums.map(n => {
                          const isCurrent = currBalls.includes(n);
                          const hitNext = nd && (nd.balls || []).includes(n);
                          return (
                            <div key={n} className={`w-12 h-12 rounded-full font-mono font-bold text-lg flex items-center justify-center transition-all ${hitNext ? 'bg-emerald-500/30 border-2 border-emerald-400 text-emerald-700 ring-1 ring-emerald-400/30' : isCurrent ? 'bg-gray-50 border border-gray-200 text-gray-300' : 'bg-red-500/10 border-2 border-red-400/20 text-red-700/80'}`}
                              data-testid={`hot-num-${n}`}>
                              {n}
                            </div>
                          );
                        })}
                        {nd && (() => {
                          const hits = hottestNums.filter(n => (nd.balls || []).includes(n));
                          return hits.length > 0 ? (
                            <div className="flex items-center ml-2">
                              <span className="text-xs font-bold text-emerald-600">{hits.length} HIT</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Block 6 — Prediction Signals */}
        {activeTab === 'main' && <BlockLabel n={6} title="Prediction Signals" subtitle="Hottest-digit, carry-over, sum/range and trending momentum cues" />}

        {/* ===== PREDICTION SIGNALS ===== */}
        {activeTab === 'main' && data.pattern_signals && (() => {
          const ps = data.pattern_signals;
          const sr = ps.sum_range || [];
          const pred = ps.sum_range_prediction;
          const co = ps.carry_over || {};
          const cwn = ps.cross_window_numbers || [];
          return (
            <div className="space-y-4" data-testid="prediction-signals">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mt-2">Prediction Signals</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* P1: Hottest Digit */}
                <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="signal-hottest">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">P1: Hottest Digit in Last 5 Draws <span className="text-emerald-600 ml-1">75% hit rate</span></div>
                  <div className="flex items-center gap-4">
                    <div className="flex gap-2">
                      {ps.hottest_digits?.map(d => (
                        <div key={d} className="w-14 h-14 rounded-xl bg-red-500/20 border-2 border-red-400/50 font-mono font-black text-2xl flex items-center justify-center text-red-700" data-testid={`hottest-d${d}`}>
                          {d}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-red-700">{ps.hottest_count}x in 5 draws</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">Numbers ending in {ps.hottest_digits?.join(', ')} are most likely next</div>
                      {nd && <div className="text-[10px] mt-1">{ps.hottest_digits?.some(d => nextDigits.has(d)) ? <span className="text-emerald-600 font-bold">HIT in next draw</span> : <span className="text-red-600/60">MISS</span>}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1 mt-3">
                    {ps.coldest_digits?.map(d => (
                      <span key={d} className="text-[10px] bg-blue-500/10 border border-blue-400/20 rounded px-2 py-0.5 text-blue-700">d{d}: {ps.coldest_count}x (coldest)</span>
                    ))}
                  </div>
                </div>

                {/* OBS 1: Digit Carry-Over */}
                <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="signal-carryover">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Digit Carry-Over <span className="text-emerald-600 ml-1">~65% carry</span></div>
                  <div className="text-[10px] text-gray-400 mb-2">All 10 digits: in current draw (carry?) vs absent (due to enter?)</div>
                  <div className="flex gap-2 flex-wrap">
                    {[...Array(10)].map((_, d) => {
                      const sticky = co.stickiness?.[String(d)];
                      const rate = sticky?.rate || 0;
                      const inCurrent = co.current_digits?.includes(d);
                      const hitNext = nd && nextDigits.has(d);
                      const l5count = parseInt(data.last_5_digit_totals?.[String(d)] || 0);
                      const isColdest = ps.coldest_digits?.includes(d);
                      return (
                        <div key={d} className={`rounded-lg px-3 py-2 text-center border ${
                          !inCurrent && isColdest ? 'bg-blue-500/15 border-blue-400/30 ring-1 ring-blue-400/20' :
                          !inCurrent ? 'bg-gray-50 border-dashed border-gray-200' :
                          rate >= 80 ? 'bg-emerald-500/15 border-emerald-400/30' :
                          rate >= 50 ? 'bg-amber-500/10 border-amber-400/20' :
                          'bg-gray-50 border-gray-200'
                        }`} data-testid={`carry-d${d}`}>
                          <div className={`text-xl font-mono font-black ${!inCurrent ? (isColdest ? 'text-blue-700' : 'text-gray-300') : ''}`}>{d}</div>
                          {inCurrent ? (
                            <div className={`text-[9px] font-bold ${rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-700' : 'text-gray-400'}`}>{rate}%</div>
                          ) : (
                            <div className={`text-[9px] font-bold ${isColdest ? 'text-blue-600' : 'text-gray-300'}`}>{l5count}x</div>
                          )}
                          {nd && inCurrent && <div className="text-[8px] mt-0.5">{hitNext ? <span className="text-emerald-600">CARRY</span> : <span className="text-red-600/50">DROP</span>}</div>}
                          {nd && !inCurrent && hitNext && <div className="text-[8px] mt-0.5 text-blue-700 font-bold">ENTER</div>}
                          {!inCurrent && isColdest && <div className="text-[7px] mt-0.5 text-blue-600">DUE</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-4 mt-2 text-[9px] text-gray-300">
                    <span>Solid = in current draw (% carry rate)</span>
                    <span>Dashed = absent</span>
                    <span className="text-blue-600">Blue = coldest (DUE)</span>
                  </div>
                </div>

                {/* OBS 5: Sum & Range */}
                <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="signal-sumrange">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Sum & Range Zigzag</div>
                  <div className="space-y-1.5">
                    {sr.map((s, i) => (
                      <div key={s.draw_number} className={`flex items-center gap-3 text-xs font-mono ${i === sr.length - 1 ? 'text-gray-900 font-bold' : 'text-gray-500'}`}>
                        <span className="w-20 text-[10px]">#{s.draw_number}</span>
                        <span className="w-16">Σ{s.sum}</span>
                        <span className="w-12">R{s.range}</span>
                        {s.sum_delta !== undefined && (
                          <>
                            <span className={`w-14 text-[10px] ${s.sum_delta > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{s.sum_delta > 0 ? '↑' : '↓'}{Math.abs(s.sum_delta)}</span>
                            <span className={`w-12 text-[10px] ${s.range_delta > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{s.range_delta > 0 ? '↑' : '↓'}{Math.abs(s.range_delta)}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  {pred && (
                    <div className="mt-3 bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-3">
                      <span className="text-[10px] text-gray-400 uppercase">Next:</span>
                      <span className={`text-xs font-bold ${pred.sum_direction === 'DOWN' ? 'text-emerald-700' : 'text-red-700'}`}>Sum {pred.sum_direction} from {pred.last_sum}</span>
                      <span className={`text-xs font-bold ${pred.range_direction === 'TIGHTER' ? 'text-emerald-700' : 'text-red-700'}`}>Range {pred.range_direction} from {pred.last_range}</span>
                      <span className="text-[10px] text-gray-300">avg={pred.avg_sum}</span>
                    </div>
                  )}
                </div>

                {/* OBS 6: Trending Up */}
                <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="signal-trending">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Trending Digits <span className="text-emerald-600 ml-1">{ps.trending_up?.length <= 3 ? '67-100%' : '33%'} hit rate</span></div>
                  <div className="text-[10px] text-gray-400 mb-2">Recent 3 draws vs Older 2 draws (within last 5)</div>
                  <div className="flex gap-4 flex-wrap">
                    <div>
                      <div className="text-[9px] text-emerald-600 uppercase mb-1 font-bold">Trending Up ({ps.trending_up?.length})</div>
                      <div className="flex gap-1.5">
                        {ps.trending_up?.map(t => {
                          const hitNext = nd && nextDigits.has(t.digit);
                          return (
                            <div key={t.digit} className={`rounded-lg px-2.5 py-2 text-center border ${hitNext ? 'bg-emerald-500/20 border-emerald-400/40' : 'bg-emerald-500/10 border-emerald-400/20'}`} data-testid={`trend-up-d${t.digit}`}>
                              <div className="text-lg font-mono font-black">{t.digit}</div>
                              <div className="text-[8px] text-gray-400">{t.older_2}→{t.recent_3}</div>
                              {nd && <div className="text-[8px] mt-0.5">{hitNext ? <span className="text-emerald-600 font-bold">HIT</span> : <span className="text-gray-300">-</span>}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-red-600 uppercase mb-1 font-bold">Trending Down ({ps.trending_down?.length})</div>
                      <div className="flex gap-1.5">
                        {ps.trending_down?.map(t => {
                          const hitNext = nd && nextDigits.has(t.digit);
                          return (
                            <div key={t.digit} className={`rounded-lg px-2.5 py-2 text-center border bg-red-500/5 border-red-400/15`} data-testid={`trend-down-d${t.digit}`}>
                              <div className="text-lg font-mono font-black text-gray-500">{t.digit}</div>
                              <div className="text-[8px] text-gray-400">{t.older_2}→{t.recent_3}</div>
                              {nd && hitNext && <div className="text-[8px] mt-0.5 text-amber-700">HIT</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {ps.trending_up?.length <= 3 && <div className="text-[9px] text-emerald-600/60 mt-2">Few trending-up digits = stronger signal</div>}
                  {ps.trending_up?.length > 3 && <div className="text-[9px] text-amber-700/60 mt-2">Many trending-up digits = signal diluted</div>}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Block A — All 5 Balls (Mains + Lucky Stars) */}
        {activeTab === 'occurrences' && <BlockLabel n="A" title="All 5 Balls — Last 3 Occurrences (Mains + LS)" subtitle="Per-ball affinity cards + cross-ball companion totals" />}

        {/* All 5 Balls — Last 3 Occurrences Window (includes LS) */}
        {activeTab === 'occurrences' && aff && (
          <AllFiveBallsView
            sourceData={data}
            cd={cd}
            aff={aff}
            testId="occurrences-window"
            headerTitle="All 5 Balls — Last 3 Occurrences Window (Mains + Lucky Stars)"
            headerBgClass="bg-gradient-to-r from-cyan-100 to-blue-50"
            headerBorderClass="border-cyan-700/40"
            headerLabelClass="text-cyan-700"
            headerDescription={`Last ${aff.occurrence_windows?.length || 3} Occurrences for every main ball (B1–B5) of draw #${cd.draw_number}. Companion counts INCLUDE numbers that appeared as Lucky Stars (LS1 / LS2) in neighbouring draws. Use this to spot cross-ball affinity signals across all 7 positions.`}
            totalsAccentClass="text-emerald-700"
            totalsBgClass="bg-emerald-50 border-emerald-300"
          />
        )}

        {/* Block B — All 5 Balls (Mains Only) */}
        {activeTab === 'occurrences_mains' && <BlockLabel n="B" title="All 5 Balls — Mains Only (Lucky Stars Excluded)" subtitle="Clean mains-only affinity signal (LS noise removed)" />}

        {/* All 5 Balls — Mains Only (no LS considered) */}
        {activeTab === 'occurrences_mains' && aff && (
          !mainsOnlyData ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="mains-loading">
              <CircleNotch size={32} className="text-emerald-600 animate-spin mx-auto mb-3" />
              <p className="text-gray-600 text-sm">Loading mains-only analysis…</p>
            </div>
          ) : (
            <AllFiveBallsView
              sourceData={mainsOnlyData}
              cd={mainsOnlyData.current_draw || cd}
              aff={mainsOnlyData.number_affinities?.[String((mainsOnlyData.current_draw?.balls || cd.balls || [])[0])] || aff}
              testId="occurrences-mains-window"
              headerTitle="All 5 Balls — Mains Only (Lucky Stars Excluded)"
              headerBgClass="bg-gradient-to-r from-emerald-100 to-green-50"
              headerBorderClass="border-emerald-700/40"
              headerLabelClass="text-emerald-700"
              headerDescription={`Same analysis as the previous window — but companion counts are computed ONLY from the 5 main balls (B1–B5) of neighbouring draws. Lucky Star positions are ignored entirely. Totals now favour numbers 13–50 that cannot be LS and were being under-counted before.`}
              totalsAccentClass="text-lime-700"
              totalsBgClass="bg-gradient-to-br from-lime-900/30 to-green-900/20 border-lime-700/40"
            />
          )
        )}

        {/* Block C — Backtest Window */}
        {activeTab === 'backtest' && <BlockLabel n="C" title="Backtest — Last 10 Draws Predictive Performance" subtitle="Digit-family & projected-number hit rates across the last 10 draws" />}

        {/* BACKTEST WINDOW — Last 10 draws predictive performance */}
        {activeTab === 'backtest' && (
          !backtestData ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="backtest-loading">
              <CircleNotch size={32} className="text-amber-600 animate-spin mx-auto mb-3" />
              <p className="text-gray-600 text-sm">{backtestLoading ? 'Running backtest across 10 historical draws…' : 'Loading backtest…'}</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="backtest-window">
              <div className="bg-amber-50 rounded-xl px-5 py-4 border border-amber-300">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Cross-Ball Companion Backtest — {backtestData.n_backtested} Draws Before #{backtestData.pivot_draw_number} ({backtestData.pivot_draw_date})</div>
                <p className="text-sm text-gray-700">
                  For each of the last {backtestData.n_backtested} draws, the Top-{backtestData.top_k} Cross-Ball Companion Totals were computed (both scopes) using data <strong>only up to that draw</strong>, then checked against the ACTUAL next draw. Hits are classified as <span className="text-cyan-700 font-bold">main</span> (appeared as B1–B5), <span className="text-yellow-700 font-bold">LS</span> (appeared as Lucky Star), or <span className="text-fuchsia-700 font-bold">digit-family</span> (a different number sharing the same last digit appeared — e.g. predicted 28, got 8/18/38/48).
                </p>
              </div>

              {/* Aggregate Summary — side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['all', 'mains_only'].map(scope => {
                  const agg = backtestData.aggregate[scope];
                  const label = scope === 'all' ? 'With LS (Mains + LS scope)' : 'Mains Only (No LS scope)';
                  const bg = scope === 'all' ? 'from-cyan-900/40 to-blue-900/30 border-cyan-700/40' : 'from-emerald-900/40 to-lime-900/30 border-emerald-700/40';
                  const accent = scope === 'all' ? 'text-cyan-700' : 'text-emerald-700';
                  return (
                    <div key={scope} className={`rounded-xl p-5 border bg-gradient-to-br ${bg}`} data-testid={`backtest-aggregate-${scope}`}>
                      <h3 className={`text-xs font-bold uppercase tracking-wider ${accent} mb-3`}>{label}</h3>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                          <div className={`text-2xl font-mono font-black ${accent}`}>{agg.avg_hit_rate_pct}%</div>
                          <div className="text-[10px] text-gray-500 mt-1">Exact Hit Rate</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-mono font-black text-gray-900">{agg.draws_with_at_least_one_hit}/{agg.draws_covered}</div>
                          <div className="text-[10px] text-gray-500 mt-1">Draws With Hit</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-mono font-black text-gray-900">{agg.total_any_hits}/{agg.total_candidates}</div>
                          <div className="text-[10px] text-gray-500 mt-1">Total Hits</div>
                        </div>
                      </div>
                      {/* Digit-Family Row */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-mono font-black text-fuchsia-700">{agg.digit_family_hit_rate_pct}%</div>
                          <div className="text-[10px] text-fuchsia-700/70 mt-1">Digit-Family Rate</div>
                        </div>
                        <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-mono font-black text-fuchsia-700">{agg.draws_with_digit_family_hit}/{agg.draws_covered}</div>
                          <div className="text-[10px] text-fuchsia-700/70 mt-1">Draws With Family Hit</div>
                        </div>
                        <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-mono font-black text-fuchsia-700">{agg.total_digit_family_hits}/{agg.total_candidates}</div>
                          <div className="text-[10px] text-fuchsia-700/70 mt-1">Family Hits (M:{agg.total_digit_family_main_hits} LS:{agg.total_digit_family_ls_hits})</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded bg-cyan-500/70" />
                          <span className="text-gray-600">Main hits: <span className="font-mono font-bold text-cyan-700">{agg.total_main_hits}</span> ({agg.main_hit_share_pct}%)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded bg-yellow-400/70" />
                          <span className="text-gray-600">LS hits: <span className="font-mono font-bold text-yellow-700">{agg.total_ls_hits}</span> ({agg.ls_hit_share_pct}%)</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-Draw Breakdown Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 bg-gray-100 border-b border-gray-200">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700">Per-Draw Breakdown</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">Each row = one historical pivot draw. Predictions from that draw's Top-{backtestData.top_k} are checked against the actual next draw.</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                        <th className="px-3 py-2 text-left">Pivot Draw</th>
                        <th className="px-3 py-2 text-left">Pivot Numbers</th>
                        <th className="px-3 py-2 text-left">Next Draw</th>
                        <th className="px-3 py-2 text-center bg-cyan-900/30">Top-5 (with LS)</th>
                        <th className="px-3 py-2 text-center bg-cyan-900/30">Hits (with LS)</th>
                        <th className="px-3 py-2 text-center bg-emerald-900/30">Top-5 (mains only)</th>
                        <th className="px-3 py-2 text-center bg-emerald-900/30">Hits (mains only)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backtestData.per_draw.map(pd => {
                        const renderTop = (scopeKey) => {
                          const s = pd.scopes[scopeKey];
                          return (
                            <div className="flex flex-wrap gap-1 justify-center">
                              {s.top.map(t => {
                                // Visual priority: exact main > exact ls > digit-family hit > miss
                                let bg;
                                let title;
                                if (t.position === 'main') {
                                  bg = 'bg-cyan-500/30 text-cyan-700 ring-1 ring-cyan-400/50';
                                  title = `Exact hit as main ball`;
                                } else if (t.position === 'ls') {
                                  bg = 'bg-yellow-500/30 text-yellow-700 ring-1 ring-yellow-400/50';
                                  title = `Exact hit as Lucky Star`;
                                } else if (t.digit_family_hit) {
                                  bg = 'bg-fuchsia-500/20 text-fuchsia-700 ring-1 ring-fuchsia-400/40';
                                  const fam = [...(t.digit_family_main || []), ...(t.digit_family_ls || [])].join(',');
                                  title = `Digit ${t.last_digit} family hit (${fam})`;
                                } else {
                                  bg = 'bg-gray-50 text-gray-500';
                                  title = `Miss (digit ${t.last_digit})`;
                                }
                                return (
                                  <span
                                    key={t.number}
                                    className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-mono font-bold ${bg}`}
                                    title={title}
                                  >
                                    {t.number}
                                    <span className="text-[8px] opacity-60">({t.total})</span>
                                    {t.digit_family_hit && t.position === null && (
                                      <span className="text-[8px] text-fuchsia-700 font-black ml-0.5">d{t.last_digit}</span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          );
                        };
                        const renderHitBadge = (scopeKey) => {
                          const s = pd.scopes[scopeKey];
                          const hitColor = s.total_hits >= 2 ? 'text-emerald-700 bg-emerald-500/20 ring-emerald-400/40'
                            : s.total_hits === 1 ? 'text-cyan-700 bg-cyan-500/20 ring-cyan-400/40'
                            : 'text-gray-400 bg-gray-50 ring-white/10';
                          const famColor = s.digit_family_candidates_hit >= 3 ? 'text-fuchsia-700 bg-fuchsia-500/20 ring-fuchsia-400/40'
                            : s.digit_family_candidates_hit >= 1 ? 'text-fuchsia-700 bg-fuchsia-500/10 ring-fuchsia-400/30'
                            : 'text-gray-400 bg-gray-50 ring-white/10';
                          return (
                            <div className="flex flex-col items-center gap-1">
                              <span className={`inline-flex items-center rounded-lg px-2 py-0.5 font-mono font-bold text-sm ring-1 ${hitColor}`} title="Exact hits">
                                {s.total_hits}/{s.top.length}
                              </span>
                              {s.total_hits > 0 && (
                                <span className="text-[9px] text-gray-500 font-mono">
                                  {s.main_hits > 0 && <span className="text-cyan-700">M:{s.main_hits}</span>}
                                  {s.main_hits > 0 && s.ls_hits > 0 && <span className="text-gray-300"> / </span>}
                                  {s.ls_hits > 0 && <span className="text-yellow-700">LS:{s.ls_hits}</span>}
                                </span>
                              )}
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono font-bold text-[10px] ring-1 ${famColor}`} title={`Digit-family hits — digits ${s.unique_digits_family_hit.join(',')} appeared`}>
                                d-fam {s.digit_family_candidates_hit}/{s.top.length}
                              </span>
                            </div>
                          );
                        };
                        return (
                          <tr key={pd.draw_number} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`backtest-row-${pd.draw_number}`}>
                            <td className="px-3 py-2 align-top">
                              <div className="font-mono font-bold text-gray-900">#{pd.draw_number}</div>
                              <div className="text-[10px] text-gray-500">{pd.draw_date}</div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex gap-1 flex-wrap">
                                {pd.pivot_balls.map(b => <span key={b} className="w-6 h-6 rounded-full bg-cyan-700/60 text-cyan-700 font-mono text-[10px] font-bold flex items-center justify-center">{b}</span>)}
                                {pd.pivot_ls.map(ls => <span key={`p-ls-${ls}`} className="w-6 h-6 rounded-full bg-yellow-700/40 text-yellow-700 font-mono text-[10px] font-bold flex items-center justify-center">{ls}</span>)}
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="font-mono font-bold text-gray-900">#{pd.next_draw_number}</div>
                              <div className="text-[10px] text-gray-500 mb-1">{pd.next_draw_date}</div>
                              <div className="flex gap-1 flex-wrap">
                                {pd.next_mains.map(b => <span key={b} className="w-6 h-6 rounded-full bg-emerald-600/70 text-emerald-50 font-mono text-[10px] font-bold flex items-center justify-center">{b}</span>)}
                                {pd.next_ls.map(ls => <span key={`n-ls-${ls}`} className="w-6 h-6 rounded-full bg-yellow-600/50 text-yellow-700 font-mono text-[10px] font-bold flex items-center justify-center">{ls}</span>)}
                              </div>
                            </td>
                            <td className="px-3 py-2 bg-cyan-900/10 align-top">{renderTop('all')}</td>
                            <td className="px-3 py-2 bg-cyan-900/10 text-center align-middle">{renderHitBadge('all')}</td>
                            <td className="px-3 py-2 bg-emerald-900/10 align-top">{renderTop('mains_only')}</td>
                            <td className="px-3 py-2 bg-emerald-900/10 text-center align-middle">{renderHitBadge('mains_only')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-5 py-3 bg-gray-100/30 text-[11px] text-gray-500 flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-cyan-500/30 ring-1 ring-cyan-400/50" /> Exact hit as main ball</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-yellow-500/30 ring-1 ring-yellow-400/50" /> Exact hit as Lucky Star</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-fuchsia-500/20 ring-1 ring-fuchsia-400/40" /> Digit-family hit (any number sharing the last digit appeared)</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-gray-50 ring-1 ring-white/10" /> Miss</span>
                  <span className="ml-auto text-gray-500 italic">Hover a badge for digit-family details</span>
                </div>
              </div>
            </div>
          )
        )}        {/* Block D — Digit Pattern Analysis */}
        {activeTab === 'distribution' && (
          <>
            <BlockLabel n="D" title="Digit Pattern Analysis" subtitle="Occurrence distributions, repeat percentages and next-draw digit sequences" />
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="w51-distribution-wrapper">
              <DigitPatternContent />
            </div>
          </>
        )}



        {/* Block 7 — Consolidated Ball Analysis */}
        {activeTab === 'main' && <BlockLabel n={7} title="Consolidated Ball Analysis — Cross-Window" subtitle="Digit matrix & convergence across projection windows" />}

        {/* CONSOLIDATED BALL ANALYSIS — at the end of the page */}
        {activeTab === 'main' && data.pattern_signals && (() => {
          const ps = data.pattern_signals;
          return (
            <div className="bg-white rounded-xl border border-cyan-300 p-5 space-y-5" data-testid="signal-crosswindow">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Consolidated Ball Analysis <span className="text-emerald-600 ml-1">Strongest Signal</span></div>

              {/* Digit Matrix Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-center">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left text-[10px] text-gray-400 uppercase px-2 py-2 w-20">Ball</th>
                      {[...Array(10)].map((_, d) => (
                        <th key={d} className="text-[10px] text-gray-500 font-bold px-1 py-2 w-12">d{d}</th>
                      ))}
                      <th className="text-[10px] text-gray-400 uppercase px-2 py-2">Hot</th>
                      <th className="text-[10px] text-gray-400 uppercase px-2 py-2">Top 3</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ps.per_ball?.map((pb, i) => {
                      const maxD = Math.max(...Object.values(pb.digit_counts || {}));
                      return (
                        <tr key={`${pb.is_ls ? 'ls' : 'main'}-${i}-${pb.ball}`} className={`border-b border-gray-100 ${pb.is_ls ? 'bg-yellow-500/[0.03]' : ''}`} data-testid={`perball-${pb.is_ls ? 'ls' : 'main'}-${i}-${pb.ball}`}>
                          <td className="px-2 py-2 text-left">
                            <div className="flex items-center gap-2">
                              <span className={`w-9 h-9 rounded-full font-mono font-bold text-sm flex items-center justify-center shrink-0 ${pb.is_ls ? 'bg-yellow-400/20 border border-yellow-400/40 text-yellow-700' : 'bg-cyan-500 text-cyan-950'}`}>
                                {pb.ball}
                              </span>
                              <span className="text-[9px] text-gray-400">{pb.label}</span>
                            </div>
                          </td>
                          {[...Array(10)].map((_, d) => {
                            const cnt = pb.digit_counts?.[d] || 0;
                            const isMax = cnt === maxD && cnt > 0;
                            return (
                              <td key={d} className="px-1 py-2">
                                <span className={`inline-flex w-8 h-8 rounded-lg font-mono text-sm font-bold items-center justify-center ${isMax ? 'bg-red-500/25 text-red-700 ring-1 ring-red-400/30' : cnt >= maxD * 0.8 ? 'bg-orange-500/15 text-orange-700' : cnt > 0 ? 'bg-gray-50 text-gray-500' : 'text-gray-300'}`}>
                                  {cnt}
                                </span>
                              </td>
                            );
                          })}
                          <td className="px-2 py-2">
                            <div className="flex gap-1 justify-center">
                              {pb.hot_digits?.map(hd => (
                                <span key={hd} className="w-7 h-7 rounded bg-red-500/20 border border-red-400/30 font-mono font-bold text-xs flex items-center justify-center text-red-700">{hd}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex gap-1 justify-center">
                              {pb.top3?.map(t => {
                                const hitNext = nd && [...(nd.balls || []), ...(nd.lucky_stars || [])].includes(t.number);
                                return (
                                  <span key={t.number} className={`w-7 h-7 rounded-full font-mono font-bold text-xs flex items-center justify-center ${hitNext ? 'bg-emerald-500/30 border border-emerald-400 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {t.number}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* AVERAGE ROW */}
                    <tr className="border-t-2 border-cyan-700/40 bg-cyan-500/[0.04]" data-testid="digit-averages-row">
                      <td className="px-2 py-3 text-left">
                        <span className="text-xs font-bold text-cyan-700 uppercase">Avg</span>
                      </td>
                      {[...Array(10)].map((_, d) => {
                        const avg = parseFloat(ps.digit_averages?.[String(d)] || 0);
                        const maxAvg = Math.max(...Object.values(ps.digit_averages || {}).map(Number));
                        const minAvg = Math.min(...Object.values(ps.digit_averages || {}).map(Number));
                        const isMax = avg === maxAvg;
                        const isMin = avg === minAvg;
                        return (
                          <td key={d} className="px-1 py-3">
                            <span className={`inline-flex w-8 h-8 rounded-lg font-mono text-sm font-black items-center justify-center ${isMax ? 'bg-red-500/30 text-red-700 ring-2 ring-red-400/40' : isMin ? 'bg-blue-500/20 text-blue-700 ring-2 ring-blue-400/30' : avg >= maxAvg * 0.85 ? 'bg-orange-500/15 text-orange-700' : 'bg-gray-50 text-gray-500'}`}>
                              {avg}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-2 py-3" colSpan={2}>
                        <div className="flex gap-3 text-[9px]">
                          <span className="text-red-700">Red = highest avg</span>
                          <span className="text-blue-700">Blue = lowest avg</span>
                        </div>
                      </td>
                    </tr>
                    {/* TOTAL ROW */}
                    <tr className="bg-gray-50" data-testid="digit-totals-row">
                      <td className="px-2 py-2 text-left">
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Total</span>
                      </td>
                      {[...Array(10)].map((_, d) => {
                        const total = parseInt(ps.digit_sums?.[String(d)] || 0);
                        const maxTotal = Math.max(...Object.values(ps.digit_sums || {}).map(Number));
                        const minTotal = Math.min(...Object.values(ps.digit_sums || {}).map(Number));
                        return (
                          <td key={d} className="px-1 py-2">
                            <span className={`inline-flex w-8 h-8 rounded-lg font-mono text-xs font-bold items-center justify-center ${total === maxTotal ? 'text-red-700' : total === minTotal ? 'text-blue-700' : 'text-gray-400'}`}>
                              {total}
                            </span>
                          </td>
                        );
                      })}
                      <td colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Digit Convergence */}
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Digit Convergence — Which digits projected by multiple balls</div>
                <div className="space-y-2">
                  {ps.digit_convergence?.filter(dc => dc.source_count >= 1).map(dc => {
                    const isMulti = dc.source_count >= 2;
                    return (
                      <div key={dc.digit} className={`flex items-center gap-3 rounded-lg px-4 py-2 ${isMulti ? 'bg-red-500/10 border border-red-400/20' : 'bg-gray-50'}`}
                        data-testid={`dconv-${dc.digit}`}>
                        <div className={`w-10 h-10 rounded-xl font-mono font-black text-xl flex items-center justify-center ${isMulti ? 'bg-red-500/25 border-2 border-red-400/40 text-red-700' : 'bg-gray-50 border border-gray-200 text-gray-500'}`}>
                          {dc.digit}
                        </div>
                        <div className="w-20 shrink-0">
                          <div className={`text-xs font-bold ${isMulti ? 'text-red-700' : 'text-gray-400'}`}>{dc.source_count} ball{dc.source_count > 1 ? 's' : ''}</div>
                          <div className="text-[8px] text-gray-300">{dc.sources?.join(', ')}</div>
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                          {dc.projected_numbers?.map(n => {
                            const hitNext = nd && (nd.balls || []).includes(n);
                            return (
                              <div key={n} className={`w-9 h-9 rounded-full font-mono font-bold text-sm flex items-center justify-center ${hitNext ? 'bg-emerald-500/30 border-2 border-emerald-400 text-emerald-700' : isMulti ? 'bg-red-500/10 border border-red-400/15 text-red-700/70' : 'bg-gray-50 border border-gray-200 text-gray-400'}`}>
                                {n}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top 3 Window Numbers Convergence */}
              {ps.top3_convergence?.length > 0 && (
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Top 3 Window Numbers — Appearing for Multiple Balls</div>
                  <div className="flex flex-wrap gap-2">
                    {ps.top3_convergence.filter(tc => tc.source_count >= 2).map(tc => {
                      const hitNext = nd && (nd.balls || []).includes(tc.number);
                      return (
                        <div key={tc.number} className={`rounded-lg px-3 py-2 text-center border ${hitNext ? 'bg-emerald-500/25 border-emerald-400/50 ring-1 ring-emerald-400/30' : 'bg-cyan-500/10 border-cyan-400/20'}`}
                          data-testid={`top3conv-${tc.number}`}>
                          <div className={`text-xl font-mono font-black ${hitNext ? 'text-emerald-700' : 'text-cyan-700'}`}>{tc.number}</div>
                          <div className="text-[8px] text-gray-400">{tc.source_count} balls</div>
                          <div className="flex justify-center gap-0.5 mt-1">
                            {tc.sources?.map((s, index) => (
                              <span key={`${tc.number}-${index}-${s}`} data-testid={`w51-convergence-${tc.number}-source-${index}`} className="text-[7px] bg-gray-100 rounded px-0.5 text-gray-400">{s}</span>
                            ))}
                          </div>
                          {hitNext && <div className="text-[8px] text-emerald-600 font-bold">HIT</div>}
                        </div>
                      );
                    })}
                    {ps.top3_convergence.filter(tc => tc.source_count >= 2).length === 0 && (
                      <div className="text-[10px] text-gray-300">No top-3 numbers shared across multiple balls</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ===== LEGACY FINAL VERDICT (hidden — superseded by new Final Verdict Table below) ===== */}
        {activeTab === 'main' && false && data.verdict?.length > 0 && (
          <div className="bg-white rounded-xl border-2 border-cyan-300 p-6 space-y-5" data-testid="final-verdict">
            <div className="text-center mb-2">
              <h2 className="text-xl font-black uppercase tracking-wider text-cyan-700">Final Verdict — Next Draw Digit Predictions</h2>
              <p className="text-[11px] text-gray-400 mt-1">Consolidated from: Last 5 Draws Frequency + Ball Convergence + Trending + Repeat Patterns</p>
            </div>

            {['DEFINITE', 'PROBABLE', 'POSSIBLE', 'WATCH'].map(level => {
              const group = data.verdict.filter(v => v.level === level);
              if (group.length === 0) return null;
              const levelStyle = {
                DEFINITE: { border: 'border-emerald-400/40', bg: 'bg-emerald-500/10', label: 'text-emerald-600', badge: 'bg-emerald-500/20 text-emerald-700 border-emerald-400/40' },
                PROBABLE: { border: 'border-amber-400/30', bg: 'bg-amber-500/5', label: 'text-amber-700', badge: 'bg-amber-500/15 text-amber-700 border-amber-400/30' },
                POSSIBLE: { border: 'border-gray-200', bg: 'bg-gray-50', label: 'text-gray-500', badge: 'bg-gray-100 text-gray-500 border-gray-200' },
                WATCH: { border: 'border-gray-200', bg: 'bg-gray-50', label: 'text-gray-400', badge: 'bg-gray-50 text-gray-400 border-gray-200' },
              }[level];
              return (
                <div key={level} className="space-y-3" data-testid={`verdict-${level.toLowerCase()}`}>
                  <div className={`text-xs font-black uppercase tracking-widest ${levelStyle.label}`}>{level}</div>
                  {group.map(v => (
                    <div key={v.digit} className={`rounded-xl border ${levelStyle.border} ${levelStyle.bg} p-4 flex flex-col md:flex-row gap-4`} data-testid={`verdict-d${v.digit}`}>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className={`w-16 h-16 rounded-2xl border-2 ${levelStyle.badge} font-mono font-black text-3xl flex items-center justify-center`}>
                          {v.digit}
                        </div>
                        <div className="space-y-0.5">
                          {v.signals?.map((s, i) => (
                            <div key={`${v.digit}-sig-${i}`} className="text-[11px] text-gray-600 flex items-start gap-1.5">
                              <span className={`mt-0.5 ${levelStyle.label}`}>&#9654;</span>
                              <span>{s}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap items-center md:ml-auto">
                        {v.projected_numbers?.map(n => {
                          const hitNext = nd && [...(nd.balls || []), ...(nd.lucky_stars || [])].includes(n);
                          return (
                            <div key={n} className={`w-11 h-11 rounded-full font-mono font-bold text-base flex items-center justify-center transition-all ${hitNext ? 'bg-emerald-500/30 border-2 border-emerald-400 text-emerald-700 ring-1 ring-emerald-400/30' : `border-2 ${levelStyle.badge}`}`}
                              data-testid={`verdict-num-${n}`}>
                              {n}
                            </div>
                          );
                        })}
                        {nd && (() => {
                          const hits = v.projected_numbers?.filter(n => [...(nd.balls || []), ...(nd.lucky_stars || [])].includes(n)) || [];
                          return hits.length > 0 ? <span className="text-xs font-bold text-emerald-600 ml-1">{hits.length} HIT</span> : null;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

      </main>
    </div>
  );
}
